import { ResponseService } from './response.service';
import { NotificationsService } from 'angular2-notifications';
import { LocalStorageService } from './../services/local-storage.service';
import { ReplaySubject } from 'rxjs/Rx';
import { ApiService } from './api.service';
import { Injectable } from '@angular/core';
import { Domain } from '../interfaces/domain.model';
import { emit } from 'cluster';
import { isFunction } from 'util';
import {
    RecordA, RecordAAAA, RecordCNAME,
    RecordPTR, RecordMX, RecordSRV,
    RecordTXT, RecordNS, RecordCAA
} from '../interfaces/domain-records/records.model';
import { DomainRecord } from '../interfaces/domain-records/record.model';
import { Validators } from '@angular/forms';
import { CustomDSValidators } from '../shared/validators/custom-validators';
import { DomainRecordOtherValue } from '../interfaces/domain-record-other-value.model';

@Injectable()
export class DomainRecordsService {

    private _companyDomainRecord = new ReplaySubject(1);
    public recordTypes = [/*'A', 'AAAA', 'CNAME', 'MX', 'SRV', */'PTR'/*, 'TXT', 'NS', 'CAA'*/];
    public domain: Domain;
    constructor(
        private apiService: ApiService,
        private localStorageService: LocalStorageService,
        private responseService: ResponseService
    ) { }

    getAllDomainRecords(domain) {
        this.apiService.get('/company/' + this.localStorageService.get('company') + '/domain/' + domain + '/record')
            .subscribe(
                (response) => {
                    this.domain = this.responseService.success(false, response, 'domain');
                    this._companyDomainRecord.next(this.domain);
                },
                (error) => {
                    this.responseService.error(error);
                }
            );

        return this._companyDomainRecord;
    }

    getStructure(type, recordValues?) {
        if (type && isFunction(this['setRecord' + type.toUpperCase()])) {
            if (!recordValues.uuid) {
                recordValues = null;
            }
            return this['setRecord' + type.toUpperCase()](recordValues);
        }
        return {};
    }

    getRecordOtherValue(propertyName, otherValues: Array<DomainRecordOtherValue>) {
        let value: any = '';
        if (otherValues) {
            otherValues.forEach(function (otherValue) {
                if (otherValue.name === propertyName) {
                    value = otherValue.value;
                }
            });
        }
        return value;
    }

    setRecordA(recordValues?) {
        let structure: RecordA;
        structure = {
            name: {
                hint: true, name: 'name', required: true,
                placeholder: 'DOMAINS.RECORDS.PLACEHOLDERS.NAME',
                title: 'DOMAINS.RECORDS.NAMES.NAME',
                value: recordValues ? recordValues.name : this.domain.name,
                valueType: 'string',
                validators: [Validators.required, CustomDSValidators.domain_name]
            },
            value: {
                hint: false, name: 'value', required: true,
                placeholder: 'DOMAINS.RECORDS.PLACEHOLDERS.A.VALUE',
                title: 'DOMAINS.RECORDS.NAMES.A.VALUE',
                value: recordValues ? recordValues.value : '',
                valueType: 'string',
                validators: [Validators.required, CustomDSValidators.IP]
            },
            ttl: {
                hint: false, name: 'ttl', required: true,
                placeholder: 'DOMAINS.RECORDS.PLACEHOLDERS.TTL',
                title: 'DOMAINS.RECORDS.NAMES.TTL',
                value: recordValues ? recordValues.ttl : this.domain.default_ttl,
                valueType: 'number',
                validators: [CustomDSValidators.default_ttl, CustomDSValidators.maxPortNumber8444]
            }
        };
        return structure;
    }

    setRecordAAAA(recordValues?) {
        const structure: RecordAAAA = {
            name: {
                hint: true, name: 'name', required: true,
                placeholder: 'DOMAINS.RECORDS.PLACEHOLDERS.NAME',
                title: 'DOMAINS.RECORDS.NAMES.NAME',
                value: recordValues ? recordValues.name : this.domain.name,
                valueType: 'string',
                validators: [Validators.required, CustomDSValidators.domain_name]
            },
            value: {
                hint: false, name: 'value', required: true,
                placeholder: 'DOMAINS.RECORDS.PLACEHOLDERS.AAAA.VALUE',
                title: 'DOMAINS.RECORDS.NAMES.AAAA.VALUE',
                value: recordValues ? recordValues.value : '',
                valueType: 'string',
                validators: [Validators.required, CustomDSValidators.IPv6]
            },
            ttl: {
                hint: false, name: 'ttl', required: true,
                placeholder: 'DOMAINS.RECORDS.PLACEHOLDERS.TTL',
                title: 'DOMAINS.RECORDS.NAMES.TTL',
                value: recordValues ? recordValues.ttl : this.domain.default_ttl,
                valueType: 'number',
                validators: [CustomDSValidators.default_ttl, CustomDSValidators.maxPortNumber8444]
            }
        };
        return structure;
    }

    setRecordCNAME(recordValues?) {
        const structure: RecordCNAME = {
            name: {
                hint: true, name: 'name', required: true,
                placeholder: 'DOMAINS.RECORDS.PLACEHOLDERS.NAME',
                title: 'DOMAINS.RECORDS.NAMES.NAME',
                value: recordValues ? recordValues.name : this.domain.name,
                valueType: 'string',
                validators: [Validators.required, CustomDSValidators.domain_name]
            },
            value: {
                hint: true, name: 'value', required: true,
                placeholder: 'DOMAINS.RECORDS.PLACEHOLDERS.CNAME.VALUE',
                title: 'DOMAINS.RECORDS.NAMES.CNAME.VALUE',
                value: recordValues ? recordValues.value : '',
                valueType: 'string',
                validators: [Validators.required, CustomDSValidators.domain_name]
            },
            ttl: {
                hint: false, name: 'ttl', required: true,
                placeholder: 'DOMAINS.RECORDS.PLACEHOLDERS.TTL',
                title: 'DOMAINS.RECORDS.NAMES.TTL',
                value: recordValues ? recordValues.ttl : this.domain.default_ttl,
                valueType: 'number',
                validators: [CustomDSValidators.default_ttl, CustomDSValidators.maxPortNumber8444]
            }
        };
        return structure;
    }

    setRecordPTR(recordValues?) {
        const structure: RecordPTR = {
            name: {
                hint: true, name: 'name', required: true,
                placeholder: 'DOMAINS.RECORDS.PLACEHOLDERS.NAME',
                title: 'DOMAINS.RECORDS.NAMES.NAME',
                value: recordValues ? recordValues.name : this.domain.name,
                valueType: 'string',
                validators: [Validators.required, CustomDSValidators.domain_name]
            },
            value: {
                hint: true, name: 'value', required: true,
                placeholder: 'DOMAINS.RECORDS.PLACEHOLDERS.VALUE',
                title: 'DOMAINS.RECORDS.NAMES.VALUE',
                value: recordValues ? recordValues.value : '',
                valueType: 'string',
                validators: [Validators.required, CustomDSValidators.domain_name]
            },
            ttl: {
                hint: false, name: 'ttl', required: true,
                placeholder: 'DOMAINS.RECORDS.PLACEHOLDERS.TTL',
                title: 'DOMAINS.RECORDS.NAMES.TTL',
                value: recordValues ? recordValues.ttl : this.domain.default_ttl,
                valueType: 'number',
                validators: [CustomDSValidators.default_ttl, CustomDSValidators.maxPortNumber8444]
            }
        };
        return structure;
    }

    setRecordMX(recordValues?) {
        const structure: RecordMX = {
            name: {
                hint: true, name: 'name', required: true,
                placeholder: 'DOMAINS.RECORDS.PLACEHOLDERS.NAME',
                title: 'DOMAINS.RECORDS.NAMES.NAME',
                value: recordValues ? recordValues.name : this.domain.name,
                valueType: 'string',
                validators: [Validators.required, CustomDSValidators.domain_name]
            },
            value: {
                hint: true, name: 'value', required: true,
                placeholder: 'DOMAINS.RECORDS.PLACEHOLDERS.VALUE',
                title: 'DOMAINS.RECORDS.NAMES.VALUE',
                value: recordValues ? recordValues.value : '',
                valueType: 'string',
                validators: [Validators.required, CustomDSValidators.domain_name]
            },
            ttl: {
                hint: false, name: 'ttl', required: true,
                placeholder: 'DOMAINS.RECORDS.PLACEHOLDERS.TTL',
                title: 'DOMAINS.RECORDS.NAMES.TTL',
                value: recordValues ? recordValues.ttl : this.domain.default_ttl,
                valueType: 'number',
                validators: [CustomDSValidators.default_ttl, CustomDSValidators.maxPortNumber8444]
            },
            preference: {
                hint: false, name: 'preference', required: true,
                placeholder: 'DOMAINS.RECORDS.PLACEHOLDERS.MX.PREFERENCE',
                title: 'DOMAINS.RECORDS.NAMES.MX.PREFERENCE',
                valueSelected: recordValues ? this.getRecordOtherValue('preference', recordValues.other_values).toString() : '10',
                value: [1, 5, 10, 15, 20, 25],
                valueType: 'selectArray',
                validators: [Validators.required, CustomDSValidators.recordPreference]
            }
        };
        return structure;
    }

    setRecordSRV(recordValues?) {

        const structure: RecordSRV = {
            name: {
                hint: true, name: 'name', required: true,
                placeholder: 'DOMAINS.RECORDS.PLACEHOLDERS.NAME',
                title: 'DOMAINS.RECORDS.NAMES.NAME',
                value: recordValues ? recordValues.name : this.domain.name,
                valueType: 'string',
                validators: [Validators.required, CustomDSValidators.domain_name]
            },
            ttl: {
                hint: false, name: 'ttl', required: true,
                placeholder: 'DOMAINS.RECORDS.PLACEHOLDERS.TTL',
                title: 'DOMAINS.RECORDS.NAMES.TTL',
                value: recordValues ? recordValues.ttl : this.domain.default_ttl,
                valueType: 'number',
                validators: [CustomDSValidators.default_ttl, CustomDSValidators.maxPortNumber8444]
            },
            priority: {
                hint: false, name: 'priority', required: true,
                placeholder: '1',
                title: 'DOMAINS.RECORDS.NAMES.SRV.PRIORITY',
                value: recordValues ? this.getRecordOtherValue('priority', recordValues.other_values) : 1,
                valueType: 'number',
                validators: [Validators.required, CustomDSValidators.number]
            },
            weight: {
                hint: false, name: 'weight', required: true,
                placeholder: '10',
                title: 'DOMAINS.RECORDS.NAMES.SRV.WEIGHT',
                value: recordValues ? this.getRecordOtherValue('weight', recordValues.other_values) : 10,
                valueType: 'number',
                validators: [Validators.required, CustomDSValidators.number]
            },
            port: {
                hint: false, name: 'port', required: true,
                placeholder: '8444',
                title: 'DOMAINS.RECORDS.NAMES.SRV.PORT',
                value: recordValues ? this.getRecordOtherValue('port', recordValues.other_values) : 8444,
                valueType: 'number',
                validators: [Validators.required, CustomDSValidators.number]
            },
            value: {
                hint: false, name: 'value', required: true,
                placeholder: 'DOMAINS.RECORDS.PLACEHOLDERS.SRV.VALUE',
                title: 'DOMAINS.RECORDS.NAMES.SRV.VALUE',
                value: recordValues ? recordValues.value : this.domain.name,
                valueType: 'string',
                validators: [Validators.required, CustomDSValidators.target_name]
            },
        };
        return structure;
    }

    setRecordTXT(recordValues?) {
        const structure: RecordTXT = {
            name: {
                hint: true, name: 'name', required: true,
                placeholder: 'DOMAINS.RECORDS.PLACEHOLDERS.NAME',
                title: 'DOMAINS.RECORDS.NAMES.NAME',
                value: recordValues ? recordValues.name : this.domain.name,
                valueType: 'string',
                validators: [Validators.required, CustomDSValidators.domain_name]
            },
            value: {
                hint: false, name: 'value', required: true,
                placeholder: 'DOMAINS.RECORDS.PLACEHOLDERS.TXT.VALUE',
                title: 'DOMAINS.RECORDS.NAMES.TXT.VALUE',
                value: recordValues ? recordValues.value : '',
                valueType: 'text',
                validators: [Validators.required]
            }
        };
        return structure;
    }

    setRecordNS(recordValues?) {
        const structure: RecordNS = {
            name: {
                hint: true, name: 'name', required: true,
                placeholder: 'DOMAINS.RECORDS.PLACEHOLDERS.NAME',
                title: 'DOMAINS.RECORDS.NAMES.NAME',
                value: recordValues ? recordValues.name : '',
                valueType: 'string',
                validators: [Validators.required, CustomDSValidators.domain_name]
            },
            value: {
                hint: true, name: 'value', required: true,
                placeholder: 'DOMAINS.RECORDS.PLACEHOLDERS.NS.VALUE',
                title: 'DOMAINS.RECORDS.NAMES.NS.VALUE',
                value: recordValues ? recordValues.value : '',
                valueType: 'string',
                validators: [Validators.required, CustomDSValidators.domain_name]
            }
        };
        return structure;
    }

    setRecordCAA(recordValues?) {
        const structure: RecordCAA = {
            name: {
                hint: false, name: 'name', required: true,
                placeholder: 'DOMAINS.RECORDS.PLACEHOLDERS.NAME',
                title: 'DOMAINS.RECORDS.NAMES.NAME',
                value: recordValues ? recordValues.name : '',
                valueType: 'string',
                validators: [Validators.required, CustomDSValidators.domain_name]
            },
            value: {
                hint: false, name: 'value', required: true,
                placeholder: 'DOMAINS.RECORDS.PLACEHOLDERS.CAA.VALUE',
                title: 'DOMAINS.RECORDS.NAMES.VALUE',
                value: recordValues ? recordValues.value : '',
                valueType: 'string',
                validators: [Validators.required, CustomDSValidators.domain_name]
            },
            flag: {
                hint: false, name: 'flag', required: true,
                placeholder: '',
                title: 'DOMAINS.RECORDS.NAMES.CAA.FLAG',
                value: [{ value: 128, label: 'Critical (128)', text: 'Flaga' }],
                valueSelected: recordValues ? this.getRecordOtherValue('flag', recordValues.other_values) : '128',
                valueType: 'checkboxObject',
                validators: [Validators.required]
            },
            tag: {
                hint: false, name: 'tag', required: false,
                placeholder: '',
                title: 'DOMAINS.RECORDS.NAMES.CAA.TAG',
                value: ['issue', 'issuewild', 'iodef'],
                valueSelected: recordValues ? this.getRecordOtherValue('tag', recordValues.other_values) : 'issue',
                valueType: 'selectArray',
                validators: [Validators.required]
            }
        };
        return structure;
    }



}



// WEBPACK FOOTER //
// ./src/app/services/domain-record.service.ts